import React, { ReactNode } from "react";
import { productBlueprintFor } from "../../game/bench-work/blueprint";
import { MaterialInstance } from "../../game/Materials";
import { SpriteThumb } from "./SpriteThumb";
import { PIXELS_PER_CELL, PIXELS_PER_INCH } from "../../views/shop-scale";
import {
  colorBySheetGoodKind,
  colorBySpecies,
} from "../../views/colorBySpecies";
import { classNames } from "../../utils/classNames";
import { formatCount } from "../../utils/formatNumber";
import { toolIconSrc } from "../../utils/uiImages";
import { Tooltip } from "../Tooltip";

/** A blueprint-assembled product spans its blueprint's footprint, which
 * is far wider than one cell — fit the icon to its longest side. */
function blueprintIconFit(type: string): number | null {
  const blueprint = productBlueprintFor(type);
  return blueprint
    ? Math.max(blueprint.widthIn, blueprint.heightIn) * PIXELS_PER_INCH
    : null;
}

// TODO: Optimize this a lot

const sizeToScale = {
  small: 0.25,
  medium: 0.5,
  large: 1,
  xl: 2,
};

export const MaterialIcon: React.FC<{
  material: MaterialInstance;
  size?: keyof typeof sizeToScale;
  quantity?: number;
  placeholder?: boolean;
  isValid?: boolean;
  tooltip?: ReactNode;
}> = ({
  material,
  size = "medium",
  quantity,
  placeholder = false,
  isValid = true,
  tooltip,
}) => {
  switch (material.type) {
    // Make the board with SVG so we don't have to use Pixi to render it
    case "board": {
      const board = material;
      const width = board.width * PIXELS_PER_INCH;
      const height = board.length * 12 * PIXELS_PER_INCH;
      const depth = (board.thickness * PIXELS_PER_INCH) / 4;

      return (
        <Wrapper
          size={size}
          quantity={quantity}
          isValid={isValid}
          tooltip={tooltip}
          placeholder={placeholder}
        >
          <svg
            viewBox={`0 0 ${PIXELS_PER_CELL} ${PIXELS_PER_CELL}`}
            className="w-full"
          >
            <rect
              x={PIXELS_PER_CELL / 2 - width / 2}
              y={PIXELS_PER_CELL / 2 - height / 2}
              width={width}
              height={height}
              fill={colorBySpecies[board.species].primary}
            />
            <rect
              x={PIXELS_PER_CELL / 2 + width / 2}
              y={PIXELS_PER_CELL / 2 - height / 2}
              width={depth}
              height={height}
              fill={colorBySpecies[board.species].secondary}
            />
          </svg>
        </Wrapper>
      );
    }

    case "plywood": {
      const board = material;
      const width = board.width * 12 * PIXELS_PER_INCH;
      const height = board.length * 12 * PIXELS_PER_INCH;
      const depth = (board.thickness * PIXELS_PER_INCH) / 4;

      return (
        <Wrapper
          size={size}
          isValid={isValid}
          tooltip={tooltip}
          placeholder={placeholder}
        >
          <svg
            viewBox={`0 0 ${PIXELS_PER_CELL} ${PIXELS_PER_CELL}`}
            className="w-full"
          >
            <rect
              x={PIXELS_PER_CELL / 2 - width / 2}
              y={PIXELS_PER_CELL / 2 - height / 2}
              width={width}
              height={height}
              fill={colorBySheetGoodKind[board.kind].primary}
            />
            <rect
              x={PIXELS_PER_CELL / 2 + width / 2 - depth}
              y={PIXELS_PER_CELL / 2 - height / 2}
              width={depth}
              height={height}
              fill={"#00000033"}
            />
          </svg>
        </Wrapper>
      );
    }

    case "pallet":
      return (
        <Wrapper
          size={size}
          isValid={isValid}
          tooltip={tooltip}
          placeholder={placeholder}
        >
          {/* Sync decode: this icon appears mid-play as the player walks
              up to a pallet, and an async decode costs it a frame. */}
          <img src="/images/pallet.png" decoding="sync" />
        </Wrapper>
      );

    case "tool":
      // The same pixel-art icon the tool wall uses — a carried tool in
      // the hands strip looks like the thing that was bought.
      return (
        <Wrapper
          size={size}
          quantity={quantity}
          isValid={isValid}
          tooltip={tooltip}
          placeholder={placeholder}
        >
          <img
            src={toolIconSrc(material.toolId)}
            decoding="sync"
            className="[image-rendering:pixelated]"
          />
        </Wrapper>
      );

    default:
      return (
        <Wrapper
          size={size}
          isValid={isValid}
          tooltip={tooltip}
          placeholder={placeholder}
        >
          <SpriteThumb
            material={material}
            size={PIXELS_PER_CELL * sizeToScale[size]}
            fit={blueprintIconFit(material.type) ?? undefined}
          />
        </Wrapper>
      );
  }
};

const sizeToClassname = {
  small: "w-8 p-0.5",
  medium: "w-12 p-1",
  large: "w-28 p-1.5",
  xl: "w-64 p-2",
};

const Wrapper: React.FC<{
  children: ReactNode;
  size: keyof typeof sizeToClassname;
  quantity?: number;
  isValid?: boolean;
  tooltip?: ReactNode;
  placeholder?: boolean;
}> = ({
  children,
  size,
  quantity,
  isValid = true,
  tooltip,
  placeholder = false,
}) => {
  // Determine border style based on state
  const borderStyle = placeholder
    ? "border-2 border-dashed border-zinc-600"
    : isValid
      ? "border border-zinc-600"
      : "border-2 border-red-500";

  const bgStyle = placeholder
    ? "bg-zinc-800/50"
    : isValid
      ? "bg-zinc-700"
      : "bg-red-900/20";

  return (
    <Tooltip content={tooltip}>
      <span
        className={classNames(
          // shrink-0 so crowded flex rows can't squash the icon
          "rounded inline-block overflow-hidden relative shrink-0",
          sizeToClassname[size],
          borderStyle,
          bgStyle,
          placeholder ? "opacity-50" : "",
        )}
      >
        {children}
        {quantity && (
          <span
            className="absolute bottom-0.5 right-1 text-xs font-condensed font-semibold tabular-nums text-white select-none"
            style={{
              textShadow:
                "1px 1px 0 #000, -1px 1px 0 #000, 1px -1px 0 #000, -1px -1px 0 #000",
            }}
          >
            {formatCount(quantity)}
          </span>
        )}
        {/* Validation indicator for invalid materials */}
        {!isValid && !placeholder && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" />
        )}
      </span>
    </Tooltip>
  );
};
